import React, { useState, useEffect, useRef } from 'react';
import { graphql } from 'gatsby';
import { OmButton } from '@om/component-library-react';
import { ThemeContext } from '../context/ThemeContext';
import './Poll.scss';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const Poll = ({ pollContent }) => {
    if (!pollContent) {
        return '';
    }

    const theme = React.useContext(ThemeContext);

    const [pollResults, setPollResults] = useState([]);
    const [voted, setVoted] = useState(false);
    const buttonRef = useRef(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [voteCount, setVoteCount] = useState(0);

    const { has_boundary, show_vote_count, heading_text, results_text, question, poll_options, action_button_text } = pollContent;

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!selectedOption) {
            console.error('Please select an option');
            return;
        }
        handleOptionSubmit(selectedOption);
    };

    const handleOptionSubmit = async (selectedValue) => {
        let data_source_path = process.env.GATSBY_ENVIRONMENT_NAME == "qa" || process.env.GATSBY_ENVIRONMENT_NAME == "local" ? `https://goals-qa.digital.omapps.net:8080/om-api/poll-response` : `https://goals-api.my.oldmutual.co.za/om-api/poll-response/`;
        if (!data_source_path || data_source_path.trim() === '') {
            return;
        }

        try {
            const postData = {
                OptionID: "",
                UUID: uuidv4(),
                PollID: "",
                Option: selectedValue,
                Question: question,
                UserSession: "Prod",
                Timestamp: new Date().toISOString()
            };

            const response = await axios.post(data_source_path, postData, { headers: { Origin: "www.oldmutual.co.za" } });
            console.log('POST response:', response);

            // Fetch updated poll results
            const updatedPollResults = await fetchPollResults();
            console.log('Updated Poll data: ', updatedPollResults);

            if (updatedPollResults) {
                const voteCounts = updatedPollResults.reduce((acc, item) => {
                    const option = item.Option;
                    if (!acc[option]) {
                        acc[option] = 0;
                    }
                    acc[option] += 1;
                    return acc;
                }, {});

                const totalVotes = Object.values(voteCounts).reduce((sum, count) => sum + count, 0);

                const mergedOptions = poll_options.map((pollOption) => {
                    const votes = voteCounts[pollOption.option_text] || 0;
                    const percentage = totalVotes ? Math.round((votes / totalVotes) * 100) : 0;
                    return { ...pollOption, votes, percentage };
                });

                setPollResults(mergedOptions);
                setVoteCount(totalVotes);
            }

            setVoted(true);
        } catch (e) {
            console.error('An error occurred while submitting the poll response:', e);
        }
    };

    const fetchPollResults = async () => {
        let data_source_path = process.env.GATSBY_ENVIRONMENT_NAME == "qa" || process.env.GATSBY_ENVIRONMENT_NAME == "local" ? `https://goals-qa.digital.omapps.net:8080/om-api/poll-response` : `https://goals-api.my.oldmutual.co.za/om-api/poll-response/`;
        
        if (!data_source_path || data_source_path.trim() === '') {
            return;
        }

        try {
            const pollResponseData = await axios.get(data_source_path, { headers: { Origin: "www.oldmutual.co.za" } });
            if (pollResponseData && pollResponseData.data && pollResponseData.status === 200) {
                console.log('Poll response data successfully retrieved');
                return pollResponseData.data.Items;
            } else {
                console.log('No poll response data retrieved from Dynamodb');
                return null;
            }
        } catch (e) {
            console.log('An error occurred when retrieving Poll Response data: ', e);
            return null;
        }
    };

    useEffect(() => {
        const getPollResults = async () => {
            const pollData = await fetchPollResults();
            console.log('Poll data: ', pollData);

            if (!pollData) return;

            const voteCounts = pollData.reduce((acc, item) => {
                const option = item.Option;
                if (!acc[option]) {
                    acc[option] = 0;
                }
                acc[option] += 1;
                return acc;
            }, {});

            const totalVotes = Object.values(voteCounts).reduce((sum, count) => sum + count, 0);

            const mergedOptions = poll_options.map((pollOption) => {
                const votes = voteCounts[pollOption.option_text] || 0;
                const percentage = totalVotes ? Math.round((votes / totalVotes) * 100) : 0;
                return { ...pollOption, votes, percentage };
            });

            setPollResults(mergedOptions);
            setVoteCount(totalVotes);
        };

        getPollResults();

        const handleButtonClick = () => {
            if (!selectedOption) {
                console.error('Please select an option');
                return;
            }
            handleOptionSubmit(selectedOption);

            setVoted(true);
            console.log('Custom button action executed!');
        };

        const buttonElement = buttonRef.current;
        if (buttonElement) {
            buttonElement.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (buttonElement) {
                buttonElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, [poll_options]);

    return (
        <section>
            <div className={`${theme} om-section`} style={{ textAlign: "center" }}>
                {has_boundary ? <div className="boundary-line-wrapper"><hr style={{ marginBottom: "0px" }} /></div> : null}
            </div>
            <div>
                <om-section>
                    <div slot="section" className="poll-wrapper">
                        <p className="poll-heading">{heading_text}</p>
                        <div className="poll-container">
                            <div className="poll-question-container">
                                <p className="poll-question">{question}</p>

                                {voted ? (
                                    <div className="poll-results-container">
                                        <p className="poll-results-heading">
                                            {results_text}
                                        </p>
                                        {show_vote_count ? (
                                            <p className="poll-results-total"> {voteCount} Votes </p>
                                        ) : null}
                                    </div>
                                ) : null}

                                {pollResults.map((option) => (
                                    <div key={option.value} className="poll-option">
                                        <label className="poll-option-container" >
                                            <input
                                                type="radio"
                                                id={option.option_text}
                                                name="pollOption"
                                                value={option.option_text}
                                                checked={selectedOption === option.option_text}
                                                onChange={handleChange}
                                                
                                            />
                                            <span className="poll-option-radio"></span>
                                            <span className="poll-option-label">{option.option_text}</span>
                                        </label>
                                        {voted ? (
                                            <div className="poll-progress-bar-wrapper">
                                                <div className="poll-progress-bar-container">
                                                    <div className="poll-progress-bar" style={{ width: `${option.percentage}%` }}>
                                                    </div>
                                                </div>
                                                <span className="poll-progress-bar-percentage"> {option.percentage}%</span>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>

                            {action_button_text && !voted ? (
                                <OmButton
                                    type="primary"
                                    size="large"
                                    text={action_button_text}
                                    iconName="arrow_forward"
                                    withIcon="true"
                                    onClick={handleSubmit}
                                    ref={buttonRef}
                                />
                            ) : null}
                        </div>
                    </div>
                </om-section>
            </div>
        </section>
    );
};

export const pageQuery = graphql`
    fragment Poll on Contentstack_poll {
        uid
        has_boundary
        show_vote_count
        question
        heading_text
        results_text
        action_button_text
        poll_options {
            ...PollOption
        }
    }

    fragment PollOption on Contentstack_polloption {
        title
        option_text
    }
`;

export default Poll;
